import React from 'react';

/**
 * Handoff banner: the page title band.
 *
 * The section nav used to live here beside the title. It moved into the top bar in Sept 2026
 * (see chrome/HeaderNav), so `showNav` now does nothing and is kept only so the pages passing
 * it don't have to change. NAV_ITEMS and activeNavForPath stay here — HeaderNav imports them.
 *
 * Background: the skybox wash this band used to keep (owner rule, DECISIONS D-9) was
 * retired with the clouds (design_handoff_lasers_background, Oct 2026): the band is now
 * transparent over the page's LASERS layer (`chrome/LasersBackground`, mounted once by
 * `AppBackground` — never a second shader here) and its type reads the page-ink variables,
 * white on lasers. The handoff's 100deg gradient is still NOT adopted. Every other value is
 * the handoff's: H1 48px/700/0.04em/lh 1, subtitle 15px, padding 34px/32px (mobile 20px/16px),
 * border-b-2.
 */
export const BANNER_BG = 'transparent';

export const NAV_ITEMS = [
  { key: 'home', label: 'Home', path: '/square' },
  { key: 'events', label: 'Events', path: '/featured' },
  { key: 'people', label: 'Friends', path: '/people' },
  { key: 'clubs', label: 'Clubs', path: '/clubs' },
  { key: 'calendar', label: 'Calendar', path: '/calendar' },
  { key: 'start', label: 'Create', path: '/start' },
];

export function activeNavForPath(pathname = '') {
  if (pathname.startsWith('/featured') || pathname.startsWith('/events')) return 'events';
  if (pathname.startsWith('/people')) return 'people';
  if (pathname.startsWith('/square')) return 'home';
  if (pathname.startsWith('/clubs')) return 'clubs';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/start') || pathname.startsWith('/portals') || pathname.startsWith('/signals')) return 'start';
  return null;
}

export default function PageBanner({
  title,
  subtitle,
  eyebrow,
  // `active` and `showNav` are accepted and ignored — the nav moved to the top bar
  // (chrome/HeaderNav) and the pages passing them don't need to change.
  // eslint-disable-next-line no-unused-vars
  active,
  // eslint-disable-next-line no-unused-vars
  showNav = true,
  actions,
  titleClassName = '',
}) {
  return (
    <div className="pr-page-border border-b-2" style={{ background: BANNER_BG }}>
      <div className="mx-auto flex max-w-content items-center justify-between gap-10 px-4 py-5 sm:px-8 lg:px-8 lg:py-[34px]">
        <div className="min-w-0">
          {eyebrow && (
            <div className="pr-page-sub mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em]">
              {eyebrow}
            </div>
          )}
          <h1 data-pr-scramble className={`pr-page-ink font-mono font-bold uppercase leading-none tracking-[0.04em] ${titleClassName || 'text-[34px] lg:text-[48px]'}`}>
            {title}
          </h1>
          {subtitle && (
            <p className="pr-page-sub mt-1.5 font-mono text-[13px] lg:mt-2.5 lg:text-[15px]">{subtitle}</p>
          )}
        </div>

        {actions && <div className="flex flex-shrink-0 items-center gap-4">{actions}</div>}
      </div>
    </div>
  );
}
